/**
 * Centralized theme configuration for consistent styling across the application.
 * Contains color palette, spacing, typography, animations, component styles, and common style patterns.
 * ALL styling for the application is defined here.
 */

/**
 * Color palette type - defines all customizable colors in the theme system.
 * Custom themes can override any of these colors.
 */
export interface ThemePalette {
  // Background surfaces
  primaryBg: string
  secondaryBg: string
  terminalBg: string
  headerBg: string

  // Border colors
  primaryBorder: string
  secondaryBorder: string
  headerBorder: string
  terminalBorder: string

  // Text colors
  primaryText: string
  secondaryText: string
  tertiaryText: string
  darkText: string
  lightText: string
  textUser: string
  headerText: string
  secondaryHeaderText: string

  // Accent palette
  visualAccent1: string
  visualAccent2: string
  visualAccent3: string
  visualAccent4: string
  primaryAccent: string
  hoverAccent: string

  // Status colors
  successBg: string
  successBorder: string
  errorBg: string
  errorBorder: string

  // Chat message colors
  assistantBg: string
  systemMsgBg: string
  userBg: string
  userMsgBg: string

  // Scrollbar colors
  scrollbarThumb: string
  scrollbarTrack: string

  // Grid overlay
  gridOverlay: string

  // Other
  white: string
  gridLine: string

  // Buddy orb states
  orbListening: string
  orbSpeaking: string
  orbProcessing: string
  orbListeningGlow: string
  orbSpeakingGlow: string
  orbProcessingGlow: string
  orbDefaultGlow: string

  // Message bubble colors with alpha
  assistantMsgBg: string
  assistantMsgGlow: string
  assistantMsgAccent: string
  userMsgBgAlpha: string
  userMsgGlow: string
  userMsgAccent: string

  // Window colors with alpha
  windowBg: string
  windowBgAlpha: string
  windowBorder: string
  windowBorderActive: string
  windowHeaderBg: string
  windowHeaderBorder: string
  windowControlBg: string
  windowControlBgHover: string

  // Navigation colors
  navBg: string
  navBorder: string
  navTabBgActive: string
  navTabBgHover: string
  navTabTextActive: string

  // Mobile nav colors
  mobileNavBg: string
  mobileNavBorder: string

  // File Explorer colors
  folderSelectedBg: string
  folderSelectedText: string
  emptyStateText: string
  mobileNavIconHover: string
  mobileNavIconActive: string

  // Web search indicator colors
  webIndicatorOnBg: string
  webIndicatorOnBorder: string
  webIndicatorOffBg: string
  webIndicatorOffBorder: string
}

/**
 * Custom theme JSON structure.
 * All themes MUST include both light and dark modes.
 */
export interface CustomThemeJson {
  light: {
    colors?: Partial<ThemePalette>
  }
  dark: {
    colors?: Partial<ThemePalette>
  }
}

/**
 * Example theme template showing all customizable color keys.
 * Copy this and modify the values to create your own theme.
 *
 * IMPORTANT: Always include both light and dark modes!
 */
export const exampleThemeTemplate: CustomThemeJson = {
  light: {
    colors: {
      // Background surfaces
      primaryBg: '#FFFFFF',
      secondaryBg: '#F2F4F7',
      terminalBg: '#FFFFFF',
      headerBg: '#E5E7EB',

      // Border colors
      primaryBorder: '#CBD5E1',
      secondaryBorder: '#E2E8F0',
      headerBorder: '#D1D5DB',
      terminalBorder: '#D1D5DB',

      // Text colors
      primaryText: '#111827',
      secondaryText: '#334155',
      tertiaryText: '#475569',
      darkText: '#111827',
      lightText: '#64748B',
      textUser: '#111827',
      headerText: '#111827',
      secondaryHeaderText: '#475569',

      // Accent colors (customize these for your theme's personality!)
      visualAccent1: '#1D4ED8',
      visualAccent2: '#2F855A',
      visualAccent3: '#C2410C',
      visualAccent4: '#8C4A2F',
      primaryAccent: '#2563EB',
      hoverAccent: '#1D4ED8',

      // Status colors
      successBg: '#ECFDF3',
      successBorder: '#16A34A',
      errorBg: '#FEF2F2',
      errorBorder: '#B91C1C',

      // Chat message colors
      assistantBg: '#F8FAFC',
      systemMsgBg: '#F1F5F9',
      userBg: '#E2E8F0',
      userMsgBg: '#CBD5F5',

      // Scrollbar
      scrollbarThumb: '#94A3B8',
      scrollbarTrack: '#E2E8F0',

      // Grid overlay
      gridOverlay: 'rgba(15, 23, 42, 0.05)',
      white: '#FFFFFF',
      gridLine: '#E5E7EB',

      // Buddy orb states
      orbListening: '#2F855A',
      orbSpeaking: '#1D4ED8',
      orbProcessing: '#C2410C',
      orbListeningGlow: 'rgba(47, 133, 90, 0.4)',
      orbSpeakingGlow: 'rgba(29, 78, 216, 0.4)',
      orbProcessingGlow: 'rgba(194, 65, 12, 0.35)',
      orbDefaultGlow: 'rgba(140, 74, 47, 0.35)',

      // Message bubbles with transparency
      assistantMsgBg: 'rgba(37, 99, 235, 0.08)',
      assistantMsgGlow: 'rgba(142, 168, 255, 0.15)',
      userMsgBgAlpha: 'rgba(47, 133, 90, 0.12)',
      userMsgGlow: 'rgba(99, 208, 166, 0.15)',

      // Window styling (including active border!)
      windowBg: '#FAFAFA',
      windowBgAlpha: 'rgba(255, 255, 255, 0.95)',
      windowBorder: 'rgba(0, 0, 0, 0.1)',
      windowBorderActive: 'rgba(0, 102, 255, 0.4)', // Active window border color
      windowHeaderBg: 'rgba(0, 0, 0, 0.025)',
      windowHeaderBorder: 'rgba(0, 0, 0, 0.08)',
      windowControlBg: 'rgba(0, 0, 0, 0.12)',
      windowControlBgHover: 'rgba(0, 0, 0, 0.2)',

      // Navigation
      navBg: 'rgba(255, 255, 255, 0.95)',
      navBorder: 'rgba(0, 0, 0, 0.15)',
      navTabBgActive: '#DBEAFE',
      navTabBgHover: '#F2F4F7',
      navTabTextActive: '#1E40AF',

      // Mobile navigation
      mobileNavBg: '#ECECEA',
      mobileNavBorder: '#C3CFD4',
      mobileNavIconHover: '#C3CFD4',
      mobileNavIconActive: '#E5B696',

      // File Explorer
      folderSelectedBg: '#2563EB', // Blue background for selected folder
      folderSelectedText: '#FFFFFF', // White text on blue background
      emptyStateText: '#6B7280', // Gray text for empty states
    },
  },
  dark: {
    colors: {
      // Background surfaces
      primaryBg: '#0B1120',
      secondaryBg: '#111827',
      terminalBg: '#0B1120',
      headerBg: '#111827',

      // Border colors
      primaryBorder: '#1F2937',
      secondaryBorder: '#1E293B',
      headerBorder: '#1F2937',
      terminalBorder: '#1F2937',

      // Text colors
      primaryText: '#F8FAFC',
      secondaryText: '#E2E8F0',
      tertiaryText: '#94A3B8',
      darkText: '#F8FAFC',
      lightText: '#94A3B8',
      textUser: '#F8FAFC',
      headerText: '#F8FAFC',
      secondaryHeaderText: '#CBD5F5',

      // Accent colors (lighter versions for dark mode)
      visualAccent1: '#8EA8FF',
      visualAccent2: '#63D0A6',
      visualAccent3: '#F7A072',
      visualAccent4: '#D4A373',
      primaryAccent: '#8EA8FF',
      hoverAccent: '#A5B9FF',

      // Status colors
      successBg: '#102A23',
      successBorder: '#63D0A6',
      errorBg: '#3B1B18',
      errorBorder: '#F7A072',

      // Chat message colors
      assistantBg: '#16213A',
      systemMsgBg: '#141F33',
      userBg: '#1F2B45',
      userMsgBg: '#26375C',

      // Scrollbar
      scrollbarThumb: '#64748B',
      scrollbarTrack: '#1E293B',

      // Grid overlay
      gridOverlay: 'rgba(248, 250, 252, 0.04)',
      white: '#0F172A',
      gridLine: '#1E293B',

      // Buddy orb states
      orbListening: '#63D0A6',
      orbSpeaking: '#8EA8FF',
      orbProcessing: '#F7A072',
      orbListeningGlow: 'rgba(99, 208, 166, 0.45)',
      orbSpeakingGlow: 'rgba(142, 168, 255, 0.45)',
      orbProcessingGlow: 'rgba(247, 160, 114, 0.4)',
      orbDefaultGlow: 'rgba(212, 163, 115, 0.4)',

      // Message bubbles with transparency
      assistantMsgBg: 'rgba(142, 168, 255, 0.08)',
      assistantMsgGlow: 'rgba(142, 168, 255, 0.15)',
      userMsgBgAlpha: 'rgba(99, 208, 166, 0.12)',
      userMsgGlow: 'rgba(99, 208, 166, 0.15)',

      // Window styling (including active border!)
      windowBg: '#1A1A1A',
      windowBgAlpha: 'rgba(0, 0, 0, 0.9)',
      windowBorder: 'rgba(255, 255, 255, 0.15)',
      windowBorderActive: 'rgba(0, 102, 255, 0.5)', // Active window border color
      windowHeaderBg: 'rgba(255, 255, 255, 0.03)',
      windowHeaderBorder: 'rgba(255, 255, 255, 0.12)',
      windowControlBg: 'rgba(255, 255, 255, 0.15)',
      windowControlBgHover: 'rgba(255, 255, 255, 0.25)',

      // Navigation
      navBg: 'rgba(0, 0, 0, 0.9)',
      navBorder: 'rgba(255, 255, 255, 0.2)',
      navTabBgActive: 'rgba(255, 255, 255, 0.08)',
      navTabBgHover: '#111827',

      // Mobile navigation
      mobileNavBg: '#111827',
      mobileNavBorder: '#1F2937',
      mobileNavIconHover: '#1F2937',
      mobileNavIconActive: '#D4A373',

      // File Explorer
      folderSelectedBg: '#3B82F6', // Brighter blue for dark mode
      folderSelectedText: '#FFFFFF', // White text for contrast
      emptyStateText: '#9CA3AF', // Lighter gray for dark mode visibility
    },
  },
}

export const lightPalette: ThemePalette = {
  // Clean background surfaces with higher contrast
  primaryBg: '#FFFFFF',
  secondaryBg: '#F2F4F7',
  terminalBg: '#FFFFFF',
  headerBg: '#E5E7EB',

  // Border colors (crisper delineation)
  primaryBorder: '#CBD5E1',
  secondaryBorder: '#E2E8F0',
  headerBorder: '#D1D5DB',
  terminalBorder: '#D1D5DB',

  // Text colors (stronger hierarchy)
  primaryText: '#111827',
  secondaryText: '#334155',
  tertiaryText: '#475569',
  darkText: '#111827',
  lightText: '#64748B',
  textUser: '#111827',
  headerText: '#111827',
  secondaryHeaderText: '#475569',

  // Accent palette (saturated, WCAG friendly)
  visualAccent1: '#1D4ED8',
  visualAccent2: '#2F855A',
  visualAccent3: '#C2410C',
  visualAccent4: '#8C4A2F',

  primaryAccent: '#2563EB',
  hoverAccent: '#1D4ED8',

  // Status colors
  successBg: '#ECFDF3',
  successBorder: '#16A34A',
  errorBg: '#FEF2F2',
  errorBorder: '#B91C1C',

  // Chat message colors
  assistantBg: '#F8FAFC',
  systemMsgBg: '#F1F5F9',
  userBg: '#E2E8F0',
  userMsgBg: '#CBD5F5',

  // Scrollbar colors
  scrollbarThumb: '#94A3B8',
  scrollbarTrack: '#E2E8F0',

  // Grid overlay
  gridOverlay: 'rgba(15, 23, 42, 0.05)',

  // Other
  white: '#FFFFFF',
  gridLine: '#E5E7EB',

  // Buddy orb states
  orbListening: '#2F855A',
  orbSpeaking: '#1D4ED8',
  orbProcessing: '#C2410C',
  orbListeningGlow: 'rgba(47, 133, 90, 0.4)',
  orbSpeakingGlow: 'rgba(29, 78, 216, 0.4)',
  orbProcessingGlow: 'rgba(194, 65, 12, 0.35)',
  orbDefaultGlow: 'rgba(140, 74, 47, 0.35)',

  // Message bubble colors with alpha
  assistantMsgBg: 'rgba(37, 99, 235, 0.08)',
  assistantMsgGlow: 'rgba(142, 168, 255, 0.15)',
  assistantMsgAccent: '#2563EB',
  userMsgBgAlpha: 'rgba(47, 133, 90, 0.12)',
  userMsgGlow: 'rgba(99, 208, 166, 0.15)',
  userMsgAccent: '#2F855A',

  // Window colors with alpha
  windowBg: '#FAFAFA',
  windowBgAlpha: 'rgba(255, 255, 255, 0.95)',
  windowBorder: 'rgba(0, 0, 0, 0.1)',
  windowBorderActive: 'rgba(0, 102, 255, 0.4)',
  windowHeaderBg: 'rgba(0, 0, 0, 0.025)',
  windowHeaderBorder: 'rgba(0, 0, 0, 0.08)',
  windowControlBg: 'rgba(0, 0, 0, 0.12)',
  windowControlBgHover: 'rgba(0, 0, 0, 0.2)',

  // Navigation colors
  navBg: 'rgba(255, 255, 255, 0.95)',
  navBorder: 'rgba(0, 0, 0, 0.15)',
  navTabBgActive: 'rgba(0, 0, 0, 0.05)',
  navTabBgHover: '#F2F4F7',

  // Mobile nav colors
  mobileNavBg: '#ECECEA',
  mobileNavBorder: '#C3CFD4',
  mobileNavIconHover: '#C3CFD4',
  mobileNavIconActive: '#E5B696',

  // File Explorer colors
  folderSelectedBg: '#2563EB',
  folderSelectedText: '#FFFFFF',
  emptyStateText: '#6B7280',

  // Web search indicator colors
  webIndicatorOnBg: 'rgba(0, 102, 255, 0.15)',
  webIndicatorOnBorder: 'rgba(0, 102, 255, 0.3)',
  webIndicatorOffBg: 'rgba(0, 0, 0, 0.05)',
  webIndicatorOffBorder: 'rgba(0, 0, 0, 0.1)',
}

export const darkPalette: ThemePalette = {
  // Dark surfaces with raised contrast
  primaryBg: '#0B1120',
  secondaryBg: '#111827',
  terminalBg: '#0B1120',
  headerBg: '#111827',

  // Border colors (visible but subtle)
  primaryBorder: '#1F2937',
  secondaryBorder: '#1E293B',
  headerBorder: '#1F2937',
  terminalBorder: '#1F2937',

  // Text hierarchy
  primaryText: '#F8FAFC',
  secondaryText: '#E2E8F0',
  tertiaryText: '#94A3B8',
  darkText: '#F8FAFC',
  lightText: '#94A3B8',
  textUser: '#F8FAFC',
  headerText: '#F8FAFC',
  secondaryHeaderText: '#CBD5F5',

  // Accents tuned for dark surfaces
  visualAccent1: '#8EA8FF',
  visualAccent2: '#63D0A6',
  visualAccent3: '#F7A072',
  visualAccent4: '#D4A373',

  primaryAccent: '#8EA8FF',
  hoverAccent: '#A5B9FF',

  // Status colors
  successBg: '#102A23',
  successBorder: '#63D0A6',
  errorBg: '#3B1B18',
  errorBorder: '#F7A072',

  // Chat message colors
  assistantBg: '#16213A',
  systemMsgBg: '#141F33',
  userBg: '#1F2B45',
  userMsgBg: '#26375C',

  // Scrollbar colors
  scrollbarThumb: '#64748B',
  scrollbarTrack: '#1E293B',

  // Grid overlay
  gridOverlay: 'rgba(248, 250, 252, 0.04)',

  // Other
  white: '#0F172A',
  gridLine: '#1E293B',

  // Buddy orb states
  orbListening: '#63D0A6',
  orbSpeaking: '#8EA8FF',
  orbProcessing: '#F7A072',
  orbListeningGlow: 'rgba(99, 208, 166, 0.45)',
  orbSpeakingGlow: 'rgba(142, 168, 255, 0.45)',
  orbProcessingGlow: 'rgba(247, 160, 114, 0.4)',
  orbDefaultGlow: 'rgba(212, 163, 115, 0.4)',

  // Message bubble colors with alpha
  assistantMsgBg: 'rgba(142, 168, 255, 0.08)',
  assistantMsgGlow: 'rgba(142, 168, 255, 0.15)',
  assistantMsgAccent: '#8EA8FF',
  userMsgBgAlpha: 'rgba(99, 208, 166, 0.12)',
  userMsgGlow: 'rgba(99, 208, 166, 0.15)',
  userMsgAccent: '#63D0A6',

  // Window colors with alpha
  windowBg: '#1A1A1A',
  windowBgAlpha: 'rgba(0, 0, 0, 0.9)',
  windowBorder: 'rgba(255, 255, 255, 0.15)',
  windowBorderActive: 'rgba(0, 102, 255, 0.5)',
  windowHeaderBg: 'rgba(255, 255, 255, 0.03)',
  windowHeaderBorder: 'rgba(255, 255, 255, 0.12)',
  windowControlBg: 'rgba(255, 255, 255, 0.15)',
  windowControlBgHover: 'rgba(255, 255, 255, 0.25)',

  // Navigation colors
  navBg: 'rgba(0, 0, 0, 0.9)',
  navBorder: 'rgba(255, 255, 255, 0.2)',
  navTabBgActive: 'rgba(255, 255, 255, 0.08)',
  navTabBgHover: '#111827',

  // Mobile nav colors
  mobileNavBg: '#111827',
  mobileNavBorder: '#1F2937',
  mobileNavIconHover: '#1F2937',
  mobileNavIconActive: '#D4A373',

  // File Explorer colors
  folderSelectedBg: '#3B82F6',
  folderSelectedText: '#FFFFFF',
  emptyStateText: '#9CA3AF',

  // Web search indicator colors
  webIndicatorOnBg: 'rgba(0, 102, 255, 0.2)',
  webIndicatorOnBorder: 'rgba(0, 102, 255, 0.4)',
  webIndicatorOffBg: 'rgba(255, 255, 255, 0.05)',
  webIndicatorOffBorder: 'rgba(255, 255, 255, 0.15)',
}

export const theme = {
  colors: { ...lightPalette } as Record<string, string>,

  darkColors: { ...darkPalette } as Record<string, string>,

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '10px',
    lg: '15px',
    xl: '20px',
    xxl: '24px',
  },

  borderRadius: {
    sm: '0px',
    md: '0px',
    lg: '0px',
    full: '9999px',
    rounded: '0.375rem',
  },

  fontSize: {
    xs: '10px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '1.5rem',
  },

  shadows: {
    // Clean, minimal shadows (like reference)
    sm: '0 1px 3px rgba(0,0,0,0.06)',
    md: '0 2px 8px rgba(0,0,0,0.08)',
    lg: '0 4px 16px rgba(0,0,0,0.12)',
    dragging: '0 8px 24px rgba(0,0,0,0.15)',
  },

  transitions: {
    default: '0.2s ease',
    boxShadow: 'box-shadow 0.2s ease, border 0.2s ease',
    transform:
      'transform 0.6s ease-in-out, background 0.3s ease, box-shadow 0.3s ease',
    bgColor: 'background-color 0.2s',
  },

  fontFamily: {
    inherit: 'inherit',
    mono: 'var(--font-departure-mono), var(--font-noto-emoji), "IBM Plex Mono", monospace',
    ibmPlex:
      'var(--font-departure-mono), var(--font-noto-emoji), "IBM Plex Mono", monospace',
    departureMono:
      'var(--font-departure-mono), var(--font-noto-emoji), monospace',
  },

  // Keyframe animations - CSS strings to be injected into global styles
  animations: {
    ripple: `@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}`,
    slideIn: `@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}`,
    windowLimitNotification: `@keyframes windowLimitNotification {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  20% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  80% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
}`,
  },

  // Animation names for use in components
  animationNames: {
    ripple: 'ripple 2s ease-out infinite',
    rippleDelayed: 'ripple 2s ease-out infinite 0.5s',
    slideIn: 'slideIn 0.3s ease-out',
  },
} as const

// Common style objects
export const commonStyles = {
  fullContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },

  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  noScrollbar: {
    overflow: 'hidden',
  },

  autoScroll: {
    overflow: 'auto',
  },

  baseTransition: {
    transition: theme.transitions.default,
  },
} as const

// Component-specific styles (converted from globals.css classes)
export const componentStyles = {
  terminalDot: {
    width: '0.75rem',
    height: '0.75rem',
    backgroundColor: theme.colors.terminalBorder,
    borderRadius: theme.borderRadius.full,
  },

  messageArea: {
    height: '60vh',
    overflowY: 'auto' as const,
    padding: '1rem',
  },

  inputArea: {
    backgroundColor: theme.colors.headerBg,
    borderTop: `1px solid ${theme.colors.headerBorder}`,
    padding: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },

  scrollbar: {
    width: '6px',
  },

  scrollbarTrack: {
    background: theme.colors.scrollbarTrack,
  },

  scrollbarThumb: {
    backgroundColor: theme.colors.scrollbarThumb,
    borderRadius: '0px',
  },

  scrollbarThumbHover: {
    backgroundColor: theme.colors.secondaryText,
  },

  windowDragging: {
    transform: 'scale(1.02)',
    boxShadow: `${theme.shadows.dragging} !important`,
  },

  mobileNav: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    height: '60px',
    backgroundColor: theme.colors.headerBg,
    borderTop: `1px solid ${theme.colors.headerBorder}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },

  mobileNavIcons: {
    display: 'flex',
    gap: '0.5rem',
    overflowX: 'auto' as const,
    padding: '0 1rem',
  },

  mobileNavIcon: {
    fontSize: theme.fontSize.xl,
    padding: '0.5rem',
    borderRadius: theme.borderRadius.rounded,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: theme.transitions.bgColor,
  },

  mobileNavIconHover: {
    backgroundColor: theme.colors.userMsgBg,
  },

  mobileNavIconActive: {
    backgroundColor: theme.colors.terminalBorder,
  },

  // StatusBar styles
  statusBar: {
    container: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      height: '32px',
      backgroundColor: theme.colors.secondaryBg,
      borderBottom: `1px solid ${theme.colors.primaryBorder}`,
      display: 'flex',
      alignItems: 'center',
      padding: `0 ${theme.spacing.sm}`,
      fontSize: theme.fontSize.xs,
      zIndex: 1,
    },
    text: {
      marginLeft: 'auto',
      color: theme.colors.secondaryText,
    },
  },

  // AuthWindow styles (colors applied via useThemeMode() in components)
  authWindow: {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: 10,
      marginTop: 0,
      padding: 0,
    },
    heading: {
      fontSize: '0.95rem',
      margin: 0,
      textAlign: 'center' as const,
    },
    form: {
      width: '100%',
      marginTop: 10,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 10,
    },
    formRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    label: {
      fontWeight: 500,
      fontSize: '0.9rem',
      width: '75px',
      minWidth: '75px',
      flexShrink: 0,
      textAlign: 'right' as const,
    },
    labelWide: {
      fontWeight: 500,
      fontSize: '0.9rem',
      width: '130px',
      minWidth: '130px',
      flexShrink: 0,
      textAlign: 'right' as const,
    },
    input: {
      padding: '8px 10px',
      borderRadius: 0,
      flex: 1,
      fontFamily: 'inherit',
      fontSize: '0.9rem',
    },
    messageContainer: {
      minHeight: '20px',
      marginBottom: '4px',
    },
    messageContainerSmall: {
      minHeight: '18px',
      marginBottom: '4px',
    },
    errorMessage: {
      margin: 0,
      fontSize: '0.8rem',
      lineHeight: '1.4',
    },
    successMessage: {
      margin: 0,
      fontSize: '0.8rem',
      lineHeight: '1.4',
    },
    buttonContainer: {
      display: 'flex',
      gap: 8,
    },
    button: {
      flex: 1,
      borderRadius: 0,
      padding: '8px 14px',
      fontWeight: 500,
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    buttonLoading: {
      flex: 1,
      borderRadius: 0,
      padding: '8px 14px',
      fontWeight: 500,
      fontSize: '0.8rem',
      cursor: 'not-allowed',
      fontFamily: 'inherit',
    },
    buttonSecondary: {
      flex: 1,
      borderRadius: 0,
      padding: '8px 14px',
      fontWeight: 500,
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    buttonSecondaryLoading: {
      flex: 1,
      borderRadius: 0,
      padding: '8px 14px',
      fontWeight: 500,
      fontSize: '0.8rem',
      cursor: 'not-allowed',
      fontFamily: 'inherit',
    },
    linkButtonContainer: {
      textAlign: 'center' as const,
      marginTop: 8,
    },
    linkButton: {
      background: 'none',
      border: 'none',
      textDecoration: 'none',
      fontSize: '0.5rem',
      fontWeight: 400,
      cursor: 'pointer',
      fontFamily: 'inherit',
      opacity: 0.7,
    },
    confirmContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 24,
      textAlign: 'center' as const,
    },
    confirmHeading: {
      fontWeight: 700,
      fontSize: '1.3rem',
      margin: 0,
    },
    confirmText: {
      fontSize: '1rem',
      margin: 0,
      maxWidth: '480px',
      lineHeight: '1.5',
    },
    confirmParagraph: {
      marginBottom: '1rem',
      margin: '0 0 1rem 0',
    },
    confirmNote: {
      margin: 0,
      fontSize: '0.9rem',
      fontStyle: 'italic' as const,
    },
    confirmButton: {
      display: 'inline-block',
      borderRadius: 0,
      padding: '10px 20px',
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: '8px',
      fontFamily: 'inherit',
    },
  },
} as const

// Base/body styles for global application
export const baseStyles = {
  body: {
    backgroundColor: theme.colors.terminalBg,
    color: theme.colors.primaryText,
    fontFamily: theme.fontFamily.ibmPlex,
    backgroundImage: `linear-gradient(${theme.colors.gridOverlay} 1px, transparent 1px), linear-gradient(90deg, ${theme.colors.gridOverlay} 1px, transparent 1px)`,
    backgroundSize: '4px 4px',
  },
} as const

// Helper function to get colors based on theme mode
export const getColors = (isDark: boolean) => {
  return isDark ? theme.darkColors : theme.colors
}

// Helper function to get dynamic base styles
export const getDynamicBaseStyles = (isDark: boolean) => {
  const colors = getColors(isDark)
  return {
    body: {
      backgroundColor: colors.terminalBg,
      color: colors.primaryText,
      fontFamily: theme.fontFamily.ibmPlex,
      backgroundImage: `linear-gradient(${colors.gridOverlay} 1px, transparent 1px), linear-gradient(90deg, ${colors.gridOverlay} 1px, transparent 1px)`,
      backgroundSize: '4px 4px',
    },
  }
}

// Runtime theme application helpers
export type RuntimeTheme = Record<string, any>

/**
 * Applies a theme JSON as CSS variables. Keys are mapped to --color-*, --spacing-*, etc.
 * - If previewRoot is provided, variables are set on that element; otherwise on :root.
 */
export function applyThemeVariables(
  themeJson: RuntimeTheme,
  previewRoot?: HTMLElement
) {
  const el = previewRoot ?? document.documentElement
  const setVar = (name: string, value: string) =>
    el.style.setProperty(name, value)

  // Colors - map to CSS variables
  const colors = themeJson.colors || {}
  Object.entries(colors).forEach(([k, v]) => {
    if (typeof v === 'string') {
      setVar(`--color-${k}`, v)
      // Also map specific keys to their CSS variable names for backwards compatibility
      const cssVarMap: Record<string, string> = {
        mobileNavBg: '--mobile-nav-bg',
        mobileNavBorder: '--mobile-nav-border',
        mobileNavIconHover: '--mobile-nav-icon-hover',
        mobileNavIconActive: '--mobile-nav-icon-active',
        scrollbarThumb: '--scrollbar-thumb',
        scrollbarTrack: '--scrollbar-track',
      }
      if (cssVarMap[k]) {
        setVar(cssVarMap[k], v)
      }
    }
  })

  // Background support
  if (themeJson.background) {
    if (themeJson.background.kind === 'solid' && themeJson.background.color) {
      setVar('--app-bg', themeJson.background.color)
    } else if (
      themeJson.background.kind === 'gradient' &&
      themeJson.background.css
    ) {
      setVar('--app-bg', themeJson.background.css)
    }
  }

  // Typography
  if (themeJson.typography?.baseSize) {
    setVar('--font-base-size', `${themeJson.typography.baseSize}px`)
  }
}

export function previewTheme(themeJson: RuntimeTheme, container: HTMLElement) {
  applyThemeVariables(themeJson, container)
}

function _mergePalette(
  target: Record<string, any>,
  overrides: Record<string, any>
) {
  if (!target || !overrides) return
  Object.entries(overrides).forEach(([k, v]) => {
    ;(target as any)[k] = v as any
  })
}

function clearThemeVariables(root?: HTMLElement) {
  const el = root ?? document.documentElement
  try {
    // Remove previously applied custom properties so old keys don't linger
    const toRemove: string[] = []
    for (let i = 0; i < el.style.length; i++) {
      const name = el.style.item(i)
      if (name.startsWith('--color-') || name.startsWith('--app-')) {
        toRemove.push(name)
      }
    }
    toRemove.forEach((name) => el.style.removeProperty(name))
  } catch {}
}

// Helper function to adjust colors for dark mode comfort
function adjustColorForDarkMode(color: string): string {
  try {
    // Convert hex to HSL for manipulation
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0,
      s = 0,
      l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }
      h /= 6
    }

    // Adjust for dark mode: reduce saturation and brightness for comfort
    s = Math.max(0, s * 0.7) // Reduce saturation by 30%
    l = Math.max(0.2, Math.min(0.8, l * 0.85)) // Reduce brightness by 15%, keep in comfortable range

    // Convert back to hex
    const hslToHex = (h: number, s: number, l: number) => {
      const a = s * Math.min(l, 1 - l)
      const f = (n: number) => {
        const k = (n + h / (1 / 12)) % 12
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, '0')
      }
      return `#${f(0)}${f(8)}${f(4)}`
    }

    return hslToHex(h, s, l)
  } catch {
    return color // Return original color if conversion fails
  }
}

export function applyThemeGlobally(themeJson: RuntimeTheme) {
  // ALL themes must have both light and dark modes
  const hasDual =
    themeJson &&
    typeof themeJson === 'object' &&
    ('light' in themeJson || 'dark' in themeJson)
  const isDark = document.documentElement.classList.contains('dark')

  // Clear any previously set CSS custom properties to avoid leftovers
  clearThemeVariables()

  let lightCfg: any
  let darkCfg: any

  if (hasDual) {
    // Proper dual-mode theme
    lightCfg = (themeJson as any).light || {}
    darkCfg = (themeJson as any).dark || {}
  } else {
    // Legacy single-mode theme - use as base for both light and dark
    console.warn(
      'Single-mode theme detected. Themes should always include both light and dark modes. Applying to both modes.'
    )
    lightCfg = themeJson
    darkCfg = themeJson
  }

  // Backwards compatibility: map old wada color names to new visualAccent names
  const mapLegacyWadaColors = (colors: any) => {
    if (!colors) return colors
    const mapped = { ...colors }
    if ('wadaIndigo' in colors) {
      mapped.visualAccent1 = colors.wadaIndigo
      delete mapped.wadaIndigo
    }
    if ('wadaSage' in colors) {
      mapped.visualAccent2 = colors.wadaSage
      delete mapped.wadaSage
    }
    if ('wadaRust' in colors) {
      mapped.visualAccent3 = colors.wadaRust
      delete mapped.wadaRust
    }
    if ('wadaClay' in colors) {
      mapped.visualAccent4 = colors.wadaClay
      delete mapped.wadaClay
    }
    return mapped
  }

  // Always update BOTH light and dark palettes
  if (lightCfg.colors) {
    Object.keys(theme.colors as any).forEach(
      (k) => delete (theme.colors as any)[k]
    )
    const mappedLightColors = mapLegacyWadaColors(lightCfg.colors)
    Object.assign(theme.colors as any, lightPalette, mappedLightColors)
  } else {
    // No custom light colors, reset to default
    Object.keys(theme.colors as any).forEach(
      (k) => delete (theme.colors as any)[k]
    )
    Object.assign(theme.colors as any, lightPalette)
  }

  if (darkCfg.colors) {
    Object.keys(theme.darkColors as any).forEach(
      (k) => delete (theme.darkColors as any)[k]
    )

    // Apply colors with dark mode adjustments
    const mappedDarkColors = mapLegacyWadaColors(darkCfg.colors)
    const adjustedDarkColors = { ...mappedDarkColors }

    // Adjust bright accent colors for dark mode comfort
    const accentKeys = [
      'primaryAccent',
      'hoverAccent',
      'visualAccent1',
      'visualAccent2',
      'visualAccent3',
      'visualAccent4',
    ]
    accentKeys.forEach((key) => {
      if (
        adjustedDarkColors[key] &&
        typeof adjustedDarkColors[key] === 'string' &&
        adjustedDarkColors[key].startsWith('#')
      ) {
        adjustedDarkColors[key] = adjustColorForDarkMode(
          adjustedDarkColors[key]
        )
      }
    })

    // Use softer text colors for dark mode
    if (!adjustedDarkColors.darkText) {
      adjustedDarkColors.darkText = '#E2E8F0' // Softer off-white instead of pure white
    }
    if (!adjustedDarkColors.primaryText) {
      adjustedDarkColors.primaryText = '#E2E8F0' // Softer primary text
    }

    Object.assign(theme.darkColors as any, darkPalette, adjustedDarkColors)
  } else {
    // No custom dark colors, reset to default
    Object.keys(theme.darkColors as any).forEach(
      (k) => delete (theme.darkColors as any)[k]
    )
    Object.assign(theme.darkColors as any, darkPalette)
  }

  // Apply CSS vars for current mode from the updated palettes
  const currentPalette = isDark ? theme.darkColors : theme.colors
  applyThemeVariables({ colors: currentPalette })

  // Notify app to recompute colors
  try {
    window.dispatchEvent(new CustomEvent('kyogre-theme-updated'))
  } catch {}
}

export function resetThemeGlobally() {
  // Restore runtime palettes to the originals
  Object.keys(theme.colors as any).forEach(
    (k) => delete (theme.colors as any)[k]
  )
  Object.assign(theme.colors as any, lightPalette)
  Object.keys(theme.darkColors as any).forEach(
    (k) => delete (theme.darkColors as any)[k]
  )
  Object.assign(theme.darkColors as any, darkPalette)
  // Optionally clear CSS variables by re-applying base palettes
  const isDark = document.documentElement.classList.contains('dark')
  const base = isDark ? { colors: darkPalette } : { colors: lightPalette }
  clearThemeVariables()
  applyThemeVariables(base)
  try {
    window.dispatchEvent(new CustomEvent('kyogre-theme-updated'))
  } catch {}
}
